import React, { useContext, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/react-hooks";
import moment from "moment";
import BackLink from "../../../components/BackLink";
import { AuthContext } from "../../../services/user/AuthProvider";
import { GENERATE_FRANCHISE_INVOICE, GET_FRANCHISE_REVENUE_AND_INVOICING } from "../services/graphql";
import franchiseStyles from "../Franchises.module.scss";
import styles from "./FranchiseFinance.module.scss";

const number = value => Number(value || 0).toLocaleString("en-GB", { maximumFractionDigits: 2 });
const statusLabel = value => value ? value.charAt(0).toUpperCase() + value.slice(1) : "—";

const FranchiseFinance = ({ franchiseId }) => {
  const { currentUser } = useContext(AuthContext);
  const submitting = useRef(false);
  const [generating, setGenerating] = useState(false);
  const [generationErrors, setGenerationErrors] = useState([]);
  const [generatedInvoice, setGeneratedInvoice] = useState("");
  const [generateInvoice] = useMutation(GENERATE_FRANCHISE_INVOICE);
  const { data, loading, error, refetch } = useQuery(GET_FRANCHISE_REVENUE_AND_INVOICING, {
    variables: { franchiseId: decodeURIComponent(franchiseId) },
    fetchPolicy: "network-only"
  });
  const revenue = data && data.franchiseRevenueAndInvoicing;
  const franchise = revenue && revenue.franchise;
  const money = (amount, currency = revenue.currency) => `${currency} ${number(amount)}`;
  const history = revenue ? revenue.invoiceHistory || [] : [];
  const progress = revenue ? Math.max(0, Math.min(100, revenue.progressPercentage)) : 0;

  const handleGenerateInvoice = async () => {
    if (submitting.current || !currentUser || currentUser.isSuperadmin !== true || !revenue || !revenue.uninvoicedSchoolsCount) return;
    submitting.current = true;
    setGenerating(true);
    setGenerationErrors([]);
    setGeneratedInvoice("");
    try {
      const { data: response } = await generateInvoice({ variables: { franchiseId: decodeURIComponent(franchiseId) } });
      const result = response && response.generateFranchiseInvoice;
      if (result && result.errors && result.errors.length) {
        setGenerationErrors(result.errors);
        return;
      }
      if (!result || !result.invoice) {
        setGenerationErrors(["Unable to generate the invoice. Please try again."]);
        return;
      }
      setGeneratedInvoice(result.invoice.invoiceNumber);
      try {
        await refetch();
      } catch (refreshError) {
        setGenerationErrors(["Invoice generated, but the page could not be refreshed. Please reload before generating another invoice."]);
      }
    } catch (requestError) {
      setGenerationErrors([requestError.message]);
    } finally {
      submitting.current = false;
      setGenerating(false);
    }
  };

  return (
    <div className={styles.finance}>
      <BackLink text={franchise ? `${franchise.name} franchise` : "Franchise"} to={`/franchises/${franchiseId}`} />
      <div className={styles.header}>
        <div>
          <h1>Revenue &amp; invoicing</h1>
          <p>Accounts view · only new or renewed paid subscriptions appear in the next invoice.</p>
        </div>
        {currentUser && currentUser.isSuperadmin === true && (
          <button type="button" className={franchiseStyles.primaryButton} onClick={handleGenerateInvoice}
            disabled={loading || !!error || generating || !revenue || !revenue.uninvoicedSchoolsCount}>
            {generating ? "Generating..." : "Generate invoice"}
          </button>
        )}
      </div>
      {generationErrors.map((message, index) => <p className={styles.message} role="alert" key={index}>{message}</p>)}
      {generatedInvoice && <p className={styles.message} role="status">Invoice {generatedInvoice} generated successfully.</p>}
      {loading && <p className={styles.message} role="status">Loading revenue and invoices...</p>}
      {error && <p className={styles.message} role="alert">Unable to load revenue and invoices. Please try again later.</p>}
      {!loading && !error && !revenue && <p className={styles.message}>Revenue and invoicing is unavailable. This page requires superadmin access.</p>}
      {!loading && !error && revenue && <>
        <section className={styles.section}>
          <div className={styles.sectionHeading}>
            <h2>Next invoice</h2>
            <p>{moment(revenue.periodStart).isBefore(moment(revenue.periodEnd), "day") ? revenue.invoiceWindow : "Invoice window: –"}</p>
          </div>
          <div className={styles.summary}>
            <div className={styles.card}>
              <h3>New fees · ex VAT</h3>
              <strong>{money(revenue.grossFeePool)}</strong>
              <p>{number(revenue.qualifyingPaidLicences)} paid pupil licences</p>
              {/* <p>{number(revenue.uninvoicedSchoolsCount)} uninvoiced schools</p> */}
            </div>
            <div className={styles.card}>
              <h3>{franchise.name} share</h3>
              <strong>{money(revenue.partnerShareAmount)}</strong>
              <p>{revenue.partnerCommissionPercentage}% progressive band</p>
            </div>
            <div className={styles.card}>
              <h3>Boom invoice due</h3>
              <strong>{money(revenue.boomShareAmount)}</strong>
              <p>New fees less {franchise.name} share</p>
            </div>
            <div className={styles.card}>
              <h3>Invoice status</h3>
              <strong className={styles.status} data-status={revenue.uninvoicedSchoolsCount > 0 ? "ready" : "empty"}>{revenue.uninvoicedSchoolsCount > 0 ? "Ready" : "Nothing to invoice"}</strong>
              {revenue.uninvoicedSchoolsCount > 0 && <p>Not yet invoiced</p>}
            </div>
          </div>
        </section>

        <section className={styles.section}>
          <h2>Commission ladder</h2>
          <div className={styles.ladderPanel}>
            <div className={styles.ladder}>
              {revenue.commissionLadder.map(band => (
                <div className={styles.band} key={band.level} data-status={band.status} data-level={band.level}>
                  <h3>{band.range}</h3>
                  <strong>{band.percentage}%</strong>
                  <p>{band.subtitle}</p>
                </div>
              ))}
            </div>
            <div className={styles.progress} role="progressbar" aria-label="Qualifying school progress"
              aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress} aria-valuetext={revenue.progressCaption}>
              <span style={{ width: `${progress}%` }} />
            </div>
            <p className={styles.progressCaption}>{revenue.progressCaption}</p>
          </div>
        </section>

        <section className={styles.section}>
          <h2>Invoice history</h2>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead><tr>
                <th scope="col">Invoice</th><th scope="col">Issued</th><th scope="col">Fee pool ex VAT</th>
                <th scope="col">{franchise.name} share</th><th scope="col">Boom invoice</th><th scope="col">Status</th>
              </tr></thead>
              <tbody>
                {history.map(invoice => (
                  <tr key={invoice.id}>
                    <th scope="row">{invoice.invoiceNumber}</th>
                    <td>{invoice.issuedDateFormatted || (invoice.issuedAt ? moment(invoice.issuedAt).format("D MMM YYYY") : "—")}</td>
                    <td>{money(invoice.grossAmount, invoice.grossAmountCurrency)}</td>
                    <td>{money(invoice.franchiseShareAmount, invoice.grossAmountCurrency)}</td>
                    <td><strong>{money(invoice.boomShareAmount, invoice.grossAmountCurrency)}</strong></td>
                    <td className={styles.status} data-status={invoice.status}>{statusLabel(invoice.status)}</td>
                  </tr>
                ))}
                {!revenue.currentInvoice && <tr>
                  <th scope="row">Draft · next run</th>
                  <td>{moment(revenue.periodEnd).format("D MMM YYYY")}</td>
                  <td>{money(revenue.grossFeePool)}</td>
                  <td>{money(revenue.partnerShareAmount)}</td>
                  <td><strong>{money(revenue.boomShareAmount)}</strong></td>
                  <td className={styles.status} data-status="draft">{statusLabel(revenue.invoiceStatus)}</td>
                </tr>}
                {history.length === 0 && revenue.currentInvoice && <tr><td colSpan={6}>No invoice history available.</td></tr>}
              </tbody>
            </table>
          </div>
        </section>

        <div className={styles.calculation}>
          <h2>Calculation rule used by accounts</h2>
          <p>Apply bands progressively: schools 1–50 = 10%; 51–99 = 15%; school 100 onwards = 20%. Count schools for life, but only invoice new or renewed paid subscriptions that have not already been included in an invoice.</p>
        </div>
      </>}
    </div>
  );
};

export default FranchiseFinance;
